import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerQuotationDetail, acceptBuyerQuotation, rejectBuyerQuotation } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

const BuyerQuotationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [q, setQ] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchBuyerQuotationDetail(parseInt(id));
      setQ(res.quotation || res);
      setItems(res.items || res.quotation?.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async () => {
    if (!id) return;
    setActionLoading(true);
    try { await acceptBuyerQuotation(parseInt(id)); load(); }
    catch (err: any) { alert(err.message || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!id) return;
    const reason = window.prompt('Reason for rejection (optional):');
    setActionLoading(true);
    try { await rejectBuyerQuotation(parseInt(id), reason || undefined); load(); }
    catch (err: any) { alert(err.message || 'Failed'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <BuyerPortalLayout title="Quotation"><div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></BuyerPortalLayout>;
  if (!q) return <BuyerPortalLayout title="Not Found"><div className="text-center py-16"><p className="text-gray-400">Quotation not found</p><Link to="/account/quotations" className="mt-3 text-sm text-emerald-600 font-bold hover:underline">← Back</Link></div></BuyerPortalLayout>;

  return (
    <BuyerPortalLayout title={`Quotation ${q.quotation_number}`}>
      <Link to="/account/quotations" className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block">← Back to Quotations</Link>

      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-extrabold text-gray-900">{q.quotation_number}</h2>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
              q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
              q.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
              q.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
            }`}>{q.status}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">From: {q.supplier_name || 'Supplier'}</p>
        </div>
        {q.status === 'submitted' && (
          <div className="flex gap-2">
            <button onClick={handleReject} disabled={actionLoading}
              className="px-4 py-2 text-sm font-bold text-red-600 border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 transition">Reject</button>
            <button onClick={handleAccept} disabled={actionLoading}
              className="px-4 py-2 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition">
              {actionLoading ? 'Processing...' : 'Accept & Order'}
            </button>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100"><tr>
            <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Item</th>
            <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Qty</th>
            <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Unit Price</th>
            <th className="px-5 py-3 text-right font-bold text-gray-500 text-xs uppercase">Total</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((item: any) => (
              <tr key={item.id}>
                <td className="px-5 py-3 font-medium text-gray-900">{item.title}</td>
                <td className="px-5 py-3 text-gray-600">{item.commercial_quantity} {item.commercial_unit}</td>
                <td className="px-5 py-3 text-gray-600">{formatMoney(Number(item.unit_price) || 0, item || DEFAULT_CURRENCY)}</td>
                <td className="px-5 py-3 text-right font-bold">{formatMoney(Number(item.total_price) || 0, item || DEFAULT_CURRENCY)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200 bg-gray-50">
            <tr><td colSpan={3} className="px-5 py-3 font-bold text-gray-900">Total</td>
              <td className="px-5 py-3 text-right font-bold text-lg">{formatMoney(Number(q.total_amount) || 0, q || DEFAULT_CURRENCY)}</td></tr>
          </tfoot>
        </table>
      </div>

      {/* Terms */}
      {(q.payment_terms || q.quality_terms || q.notes) && (
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Terms & Notes</h3>
          <div className="space-y-2 text-sm">
            {q.payment_terms && <div><p className="font-medium text-gray-600">Payment Terms</p><p className="text-gray-500">{q.payment_terms}</p></div>}
            {q.quality_terms && <div><p className="font-medium text-gray-600">Quality Terms</p><p className="text-gray-500">{q.quality_terms}</p></div>}
            {q.notes && <div><p className="font-medium text-gray-600">Notes</p><p className="text-gray-500">{q.notes}</p></div>}
          </div>
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerQuotationDetailPage;
